import { AxiosRequestConfig } from "axios";
import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { axiosBaseConfig } from "../../../configs";
import { merchantApiBaseUrl, swAppCombinedUrls } from "../../../constants";
import { ISwAppApi, ISwAppServiceApi } from "../../../interfaces-and-types";
import { logDataToConsole } from "../../../utils";
import { useActivityBlocker } from "../../useActivityBlocker";
import {
  ISwitchappAxiosClientConfig,
  useSwitchappAxiosClient,
} from "../useSwitchappAxiosClient";
import { useAuth } from "../../useAuth";
import { useNotifications } from "../../useNotifications";

type TMutationObserverOption<TData> = UseMutationOptions<
  TData,
  { data: any | null } | null,
  unknown,
  any
> & {};

// type to enable muxing post, put, patch, and delete mutations for the template
type TMutationFn<TRequestData, TResponseData> =
  | ((id?: string, data?: TRequestData | null) => Promise<TResponseData>)
  | ((data?: TRequestData | null) => Promise<TResponseData>);

export function useMerchantApiMutation<TRequestData, TResponseData>(
  queryKey: keyof ISwAppApi,
  refetchQueryKeys: (keyof ISwAppApi)[] | null = null,
  axiosConfig:
    | ISwitchappAxiosClientConfig<TRequestData>
    | undefined = undefined, // to set multipart/form-data,
  getQueryUrl: (templateQueryUrl: string) => string = (val) => val,
  queryConfig: TMutationObserverOption<
    ISwAppServiceApi<TResponseData>
  > | null = null
) {
  const baseURL = merchantApiBaseUrl;
  const { active_tenant_id } = useAuth();
  const ignoreActivityBlocker = axiosConfig?.ignoreActivityBlocker;

  const axiosClient = useSwitchappAxiosClient(
    {
      baseURL,
      ...axiosBaseConfig,
      ...axiosConfig,
    },
    active_tenant_id ? active_tenant_id : undefined
  );
  const queryClient = useQueryClient();
  const { clearBlocked, setBlocked } = useActivityBlocker();

  const notificationsContext = useNotifications();

  function displaySuccessMessage(title: string, message: string) {
    // logDataToConsole(message, title);

    if (!axiosConfig?.hideResponseMessage)
      notificationsContext.showNotification({
        ariaLabel: "success notification",
        title: title,
        kind: "success",
        message: message,
        statusIconDescription: "success",
        timeout: 6000,
      });
  }

  function getQueryHandler(
    customGetQueryUrl: (templateQueryUrl: string) => string = (val) => val
  ) {
    const url = customGetQueryUrl
      ? customGetQueryUrl(swAppCombinedUrls[queryKey])
      : getQueryUrl(swAppCombinedUrls[queryKey]);

    return axiosClient
      .get<ISwAppServiceApi<TResponseData>>(url)
      .then((response) => response.data); // NOTE: react query catches and handles our errors
  }

  function postMutation(data: TRequestData | null = null) {
    const url = getQueryUrl(swAppCombinedUrls[queryKey]);

    return axiosClient
      .post<ISwAppServiceApi<TResponseData>>(url, data)
      .then((response) => response.data); // NOTE: react query catches and handles our errors
  }

  function putMutation(data: TRequestData | null = null) {
    const url = getQueryUrl(swAppCombinedUrls[queryKey]);

    return axiosClient
      .put<ISwAppServiceApi<TResponseData>>(url, data)
      .then((response) => response.data);
  }

  function deleteMutation() {
    const url = getQueryUrl(swAppCombinedUrls[queryKey]);

    return axiosClient
      .delete<ISwAppServiceApi<TResponseData>>(url)
      .then((response) => response.data);
  }

  // HOOK IMPLEMENTATIONS
  const useGetQueryHandler = useMutation([queryKey], getQueryHandler, {
    onSuccess: (data) => {
      // invalidate all queries linked to this mutation
      refetchQueryKeys &&
        refetchQueryKeys.length > 0 &&
        refetchQueryKeys.map((qK) => queryClient.invalidateQueries([qK]));
    },
    onError: (error: { data: any | null } | null) => {
      // console.log("error?.data in post Mutation: ", error?.data);
      return error?.data;
    },
    onMutate: (data) => {},
  });

  const usePostMutation = useMutation([queryKey], postMutation, {
    onSuccess: (data) => {
      !ignoreActivityBlocker && clearBlocked();

      displaySuccessMessage("Message", data.message);

      // invalidate all queries linked to this mutation
      refetchQueryKeys &&
        refetchQueryKeys.length > 0 &&
        refetchQueryKeys.map((qK) => queryClient.invalidateQueries([qK]));
    },
    // onError: (error: { data: ISwAppServiceApi<any | null> } | null) => {
    onError: (error: { data: any | null } | null) => {
      !ignoreActivityBlocker && clearBlocked();

      // console.log("error?.data in post Mutation: ", error?.data);
      return error?.data;
    },
    onMutate: (data) => {
      !ignoreActivityBlocker && setBlocked();
    },
    ...queryConfig,
  });

  const usePutMutation = useMutation([queryKey], putMutation, {
    onSuccess: (data) => {
      !ignoreActivityBlocker && clearBlocked();

      displaySuccessMessage("Message", data.message);

      refetchQueryKeys &&
        refetchQueryKeys.length > 0 &&
        refetchQueryKeys.map((qK) => queryClient.invalidateQueries([qK]));
    },
    onError: (error: { data: any | null } | null) => {
      !ignoreActivityBlocker && clearBlocked();

      return error?.data;
    },
    onMutate: (data) => {
      !ignoreActivityBlocker && setBlocked();
    },
  });

  const useDeleteMutation = useMutation([queryKey], deleteMutation, {
    onSuccess: (data) => {
      !ignoreActivityBlocker && clearBlocked();

      displaySuccessMessage("Message", data.message);

      refetchQueryKeys &&
        refetchQueryKeys.length > 0 &&
        refetchQueryKeys.map((qK) => queryClient.invalidateQueries([qK]));
    },
    onError: (error: { data: any | null } | null) => {
      !ignoreActivityBlocker && clearBlocked();

      return error?.data;
    },
    onMutate: (data) => {
      !ignoreActivityBlocker && setBlocked();
    },
  });

  return {
    useGetQueryHandler,
    usePostMutation,
    usePutMutation,
    useDeleteMutation,
  };
}
